import { useOrg } from '@/admin/hooks/use-org'

/**
 * Known feature flag keys.  Add new flags here to get compile-time safety.
 * Each key corresponds to a row in the backend FeatureFlagDefinition table,
 * and a boolean entry in Tenant.feature_flags JSONField.
 */
/**
 * Canonical flag constants - single source of truth for all flag keys.
 * Import `FLAGS` and use `FLAGS.PRODUCTS` instead of raw strings.
 */
export const FLAGS = {
  PRODUCTS: 'products',
  POLICIES_TAB: 'policies_tab',
  CLAIMS: 'claims',
  LAB: 'lab',
  REGISTRATIONS: 'registrations',
  POSTCODE_AUTOFILL: 'postcode_autofill',
  PRORATION_FORMULAS: 'proration_formulas',
  GEOGRAPHIC_AVAILABILITY: 'geographic_availability',
  TRANSFERS: 'transfers',
  EXCEPTIONS: 'exceptions',
  PUBLIC_API: 'public_api',
  INTEGRATIONS: 'integrations',
  SMS: 'sms',
  REWARDS: 'rewards',
  REQUIRE_DEALER_ON_REGISTRATION: 'require_dealer_on_registration',
  WARRANTY_CLASS_LABELS: 'warranty_class_labels',
  PAID_REGISTRATIONS: 'paid_registrations',
  REGISTRATION_RULES: 'registration_rules',
  REGISTRATION_FIELDS: 'registration_fields',
  WORKFLOW_DESIGNER: 'workflow_designer',
  BRANDS: 'brands',
  EMAIL_AUTHORING: 'email_authoring',
  EMAIL_TRIGGERS: 'email_triggers',
  EVIDENCE_REQUESTS: 'evidence_requests',
  EMAIL_TRIGGERS_API: 'email_triggers_api',
  FAULT_DEFINITIONS: 'fault_definitions',
  INSPECTIONS: 'inspections',
  SCHEDULING: 'scheduling',
  PRODUCT_IDENTIFICATION: 'product_identification',
  PULSE_WEBHOOKS: 'pulse_webhooks',
  PULSE_EMAIL_TRIGGERS: 'pulse_email_triggers',
  PULSE_WORKFLOW_ACTIONS: 'pulse_workflow_actions',
  PULSE_SMS: 'pulse_sms',
  VOICE: 'voice',
  APPROVALS: 'approvals',
  DUPLICATES: 'duplicates',
  CLAIM_AUTO_ASSIGN: 'claim_auto_assign',
  BULK_IMPORTS: 'bulk_imports',
  REGISTRATION_GROUPS: 'registration_groups',
  HOA: 'hoa',
  SETTLEMENT: 'settlement',
  CYCLETIME: 'cycletime',
  ASSESSMENT: 'assessment',
  MULTI_ITEM_CLAIMS: 'multi_item_claims',
  PUBLIC_CLAIM_INTAKE: 'public_claim_intake',
  IDENTIFIERS: 'identifiers',
  PARTNER_BRANCHING: 'partner_branching',
  COMMUNICATIONS: 'communications',
  AUTO_REMINDERS: 'auto_reminders',
  TIERS: 'tiers',
  VOUCHERS: 'vouchers',
} as const

export type FeatureFlagKey = (typeof FLAGS)[keyof typeof FLAGS]

/**
 * Returns whether a single feature flag is enabled for the current tenant.
 * Reads from the org context (no additional API call).
 * Missing or falsy flags return `false` (fail-closed).
 *
 * @example
 *   const hasTransfers = useFeatureFlag('transfers')
 *   if (!hasTransfers) return null
 */
export function useFeatureFlag(key: FeatureFlagKey): boolean {
  const { data } = useOrg()
  return data?.tenant_feature_flags?.[key] === true
}

/**
 * Returns the full flag map.  Useful when checking multiple flags at once.
 */
export function useFeatureFlags(): Record<string, boolean> {
  const { data } = useOrg()
  return (data?.tenant_feature_flags as Record<string, boolean>) ?? {}
}
